import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	listSentPendingSharesHousesHouseIdDocumentsPendingSentGetQueryKey,
	shareDocumentsWithOwnerEndpointHousesHouseIdDocumentsSharePostMutation,
} from '@/client/@tanstack/react-query.gen';

/**
 * Hook to share multiple documents with the property owner in a single batch operation.
 *
 * Purpose: Replaces single-document sharing with bulk sharing to reduce API calls
 * and improve performance when sharing multiple documents after property transfer.
 */
export const useShareDocumentsWithOwner = (houseId: number) => {
	const queryClient = useQueryClient();
	const mutation = useMutation(shareDocumentsWithOwnerEndpointHousesHouseIdDocumentsSharePostMutation());

	const shareDocuments = async (documentIds: number[], message?: string) => {
		const result = await mutation.mutateAsync({
			body: {
				document_ids: documentIds,
				message: message ?? null,
			},
			path: { house_id: houseId },
		});
		// Fire-and-forget: the sent-shares list refetches in the background so callers (the send dialog,
		// the bulk-share bar) settle as soon as the POST lands instead of waiting on the refetch tail.
		void queryClient.invalidateQueries({
			queryKey: listSentPendingSharesHousesHouseIdDocumentsPendingSentGetQueryKey({
				path: { house_id: houseId },
			}),
		});
		return result;
	};

	return { shareDocuments, ...mutation };
};

export default useShareDocumentsWithOwner;
